<template>
  <div class="page-template-shipping">
    <div class="page-template-shipping__two">
      <!-- LEFT COLUMN -->
      <div class="page-template-shipping__two-col" v-if="left">
        <div class="page-template-shipping__details" v-if="left.title_1 || left.content_1">
          <h2 class="page-template-shipping__title" v-if="left.title_1">{{ left.title_1 }}</h2>
          <div
            class="page-template-shipping__content rte"
            v-if="left.content_1"
            v-html="left.content_1"
          />
        </div>

        <div class="page-template-shipping__details" v-if="left.title_2 || left.content_2">
          <h2 class="page-template-shipping__title" v-if="left.title_2">{{ left.title_2 }}</h2>
          <div
            class="page-template-shipping__content rte"
            v-if="left.content_2"
            v-html="left.content_2"
          />
        </div>

        <div class="page-template-shipping__details" v-if="left.title_3 || left.content_3">
          <h2 class="page-template-shipping__title" v-if="left.title_3">{{ left.title_3 }}</h2>
          <div
            class="page-template-shipping__content rte"
            v-if="left.content_3"
            v-html="left.content_3"
          />
        </div>

        <div class="page-template-shipping__image" v-if="left.imageUrl">
          <figure class="shipping__thumb">
            <CommonImage :src="left.imageUrl" :alt="left.imageAlt || ''" />
          </figure>
        </div>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="page-template-shipping__two-col" v-if="right">
        <div class="page-template-shipping__details" v-if="right.title_1 || right.content_1">
          <h2 class="page-template-shipping__title" id="returns" v-if="right.title_1">
            {{ right.title_1 }}
          </h2>
          <div
            class="page-template-shipping__content rte"
            v-if="right.content_1"
            v-html="right.content_1"
          />
        </div>

        <div class="page-template-shipping__details" v-if="right.title_2 || right.content_2">
          <h2 class="page-template-shipping__title" id="fulfillment" v-if="right.title_2">
            {{ right.title_2 }}
          </h2>
          <div
            class="page-template-shipping__content rte"
            v-if="right.content_2"
            v-html="right.content_2"
          />
        </div>

        <div class="page-template-shipping__details" v-if="right.title_3 || right.content_3">
          <h2 class="page-template-shipping__title" id="insurance" v-if="right.title_3">
            {{ right.title_3 }}
          </h2>
          <div
            class="page-template-shipping__content rte"
            v-if="right.content_3"
            v-html="right.content_3"
          />
        </div>

        <div class="page-template-shipping__details" v-if="right.title_4 || right.content_4">
          <h2 class="page-template-shipping__title" id="shipping" v-if="right.title_4">
            {{ right.title_4 }}
          </h2>
          <div
            class="page-template-shipping__content rte"
            v-if="right.content_4"
            v-html="right.content_4"
          />
        </div>

        <div class="page-template-shipping__details" v-if="right.title_5 || right.content_5">
          <h2 class="page-template-shipping__title" id="address" v-if="right.title_5">
            {{ right.title_5 }}
          </h2>
          <div
            class="page-template-shipping__content rte"
            v-if="right.content_5"
            v-html="right.content_5"
          />
        </div>

        <div class="page-template-shipping__details" v-if="right.title_6 || right.content_6">
          <h2 class="page-template-shipping__title" id="warranty" v-if="right.title_6">
            {{ right.title_6 }}
          </h2>
          <div
            class="page-template-shipping__content rte"
            v-if="right.content_6"
            v-html="right.content_6"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CommonImage from '../common/CommonImage.vue'

type LeftContentBlock = {
  type: 'left_content'
  title_1: string
  content_1: string
  title_2: string
  content_2: string
  title_3: string
  content_3: string
  imageUrl?: string
  imageAlt?: string
}

type RightContentBlock = {
  type: 'right_content'
  title_1: string
  content_1: string
  title_2: string
  content_2: string
  title_3: string
  content_3: string
  title_4: string
  content_4: string
  title_5: string
  content_5: string
  title_6: string
  content_6: string
}

const props = withDefaults(
  defineProps<{
    blocks?: Array<LeftContentBlock | RightContentBlock>
  }>(),
  {
    blocks: () => [],
  }
)

const left = props.blocks.find((b) => b.type === 'left_content') as LeftContentBlock | undefined
const right = props.blocks.find((b) => b.type === 'right_content') as RightContentBlock | undefined
</script>

<style lang="scss">
@import '~styles/base.scss';

.page-template-shipping {
  /* overall page padding + centering similar to other pages */
  @include wrapper;
  padding: 0 var(--inset);

  @include breakpoint('xl') {
    margin: 0;
  }

  /* two columns, left narrow, right wide */
  &__two {
    display: grid;
    /* tweak these to taste if your columns() helper is preferred */
    grid-template-columns: 8fr 4fr;
    column-gap: columns(1, 0);
    row-gap: 40px;
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 80px;

    @include breakpoint('xl') {
      grid-template-columns: 1fr;
      row-gap: 60px;
    }

    @include breakpoint('l') {
      padding: 0 0 !important;
    }
  }

  /* LEFT COLUMN (sidebar look) */

  @include breakpoint('xl') {
    &__two-col:first-child {
      align-self: start;
      margin-right: 0px !important;
    }
  }

  @include breakpoint('xl+') {
  }

  &__two-col:first-child {
    align-self: start;
    margin-right: 80px;

    /* NEW: group 2nd + 3rd details horizontally */
    .page-template-shipping__details:nth-of-type(2),
    .page-template-shipping__details:nth-of-type(3) {
      display: inline-block;
      vertical-align: top;
      width: calc(50% - 10px); /* side by side, with a little gap */
    }

    /* add spacing between them */
    .page-template-shipping__details:nth-of-type(2) {
      margin-right: 20px;
    }

    /* three stacked blocks: SHIPPING / CUSTOMER SERVICE / INDEX */
    .page-template-shipping__details {
      margin-bottom: 16px;

      /* small uppercase headings */
      .page-template-shipping__title {
        margin: 0 0 4px;
        @include type-xxs-uppercase;
      }

      /* tidy text rhythm */
      .page-template-shipping__content {
        white-space: normal;

        a {
          line-height: var(--font-lh-s);
        }

        p {
          margin: 0 0 1.5em;
        }
        p:last-child {
          margin-bottom: 0;
        }

        /* make any list from richtext look like the index in the comp */
        ul {
          list-style: none;
          padding-left: 0;
          margin: 0;
        }
        li + li {
          margin-top: 4px;
        }
      }
    }
  }

  /* small thumb under the left text */
  &__image {
    position: static;
    margin-top: 10px;

    figure,
    img {
      display: block;
      width: 110px; /* visually matches the comp; adjust if needed */
      max-width: 100%;
      height: auto;
      object-fit: cover;
      aspect-ratio: 4 / 5;
    }
  }

  @include breakpoint('l') {
    &__image {
      position: absolute;
      top: 300px;
    }
  }

  @include breakpoint('xl+') {
    &__image {
      position: absolute;
      top: 390px;
    }
  }

  /* RIGHT COLUMN (content sections) */

  .page-template-shipping__title {
    scroll-margin-top: var(--header-height, 225px);
  }

  &__two-col {
    @include breakpoint('l') {
      padding-right: calc(var(--column) * 3 + var(--gutter) * 0);
    }
  }

  &__two-col:first-child {
    @include breakpoint('l') {
      padding-right: 0 !important;
    }
  }

  &__two-col:last-child {
    align-self: start;
    /* create some breathing room from the left column on wide screens */
    padding-left: columns(1, 0);

    @include breakpoint('l') {
      padding-top: 120px;
    }

    @include breakpoint('xl') {
      padding-left: 0;
    }

    .page-template-shipping__details {
      margin-bottom: 100px;
    }

    .page-template-shipping__title {
      margin: 0 0 4px;
      @include type-xxs-uppercase;
    }

    .page-template-shipping__content {
      white-space: normal;

      p {
        margin: 0 0 1.5em;
      }
      p:last-child {
        margin-bottom: 0;
      }
    }

    a:not([target='_blank']) {
      text-decoration: underline;
    }
  }
}
</style>
